import { useCallback, useEffect, useState, type ReactNode } from "react";

import type { AddressRecord, DriverApi, StopPlanRecord } from "../driver-api.ts";
import { ADDRESS_READ_SCOPE, DISTANCE_SCOPE, PICKUP_TRAVEL_MODE } from "../pickup.ts";
import {
  ARRIVAL_CONFLICT_SUB,
  ARRIVAL_CONFLICT_TITLE,
  ARRIVE_AT_STOP,
  arriveLabel,
  arriveUnavailable,
  NO_COURIER_SUB,
  NO_COURIER_TITLE,
  NO_PLAN_SUB,
  NO_PLAN_TITLE,
  planChip,
  planChipIsDesigned,
  planInProgress,
  planRows,
  planSubLine,
  planTitle,
  stopProgress,
  STOP_PLAN_READ_SCOPE,
  STOP_TRANSITION_SCOPE,
} from "../stop-plan.ts";
import { stateForError, type ViewState } from "../view-state.ts";
import { PhoneShell } from "./PhoneShell.tsx";
import { StateScreen } from "./StateScreen.tsx";

/**
 * `Multi-stop job` — the plan a courier is carrying, and the stop they are at.
 *
 * The design: the map hero with nothing drawn in it, the `Online` pill, the
 * `['active','stop 2 of 5']` chip, _"Oyster Bay, Plot 44"_, _"Then 3 more drops · 6.2 km left"_,
 * three key/value rows and the slide-to-confirm `Arrived at stop 2`.
 *
 * `src/stop-plan.ts` carries the reasoning for every drawn value.
 * `tests/contract/mobile-screen-support.test.ts` marks this screen **buildable with nothing
 * missing**, which it was not until
 * [ADR 0040](../../../docs/adr/0040-a-courier-finds-its-stop-plan-through-a-listing-bounded-by-its-own-subject.md)
 * gave a courier a way to find their own plan.
 *
 * ## Three reads and a sum, in that order, and each one's failure is its own
 *
 * The **listing** answers the subject. Without it there is no screen, so its failure is the screen's.
 *
 * The **addresses** answer the places. Each is settled independently and an address that could not be
 * read costs its own row and nothing else — a plan whose third stop is unreadable still draws the
 * stop in hand, because the courier is standing at it. Resolved as a distinct set rather than per
 * stop: two stops at one address are one question, which is the same restraint TRP-04's own
 * `confirmAddresses` applies to the same identifiers going the other way.
 *
 * The **distance** answers the second half of one line and is the only thing here that is a sum.
 * GEO-04 answers one origin-and-destination pair, and the design's `6.2 km left` is a route — so the
 * legs are added up: this device to the stop in hand, then stop to stop through the rest. If any leg
 * fails the figure is dropped **entirely**, because a partial sum presented as a total is the one
 * thing a courier must not be given; the line then draws its first clause and stops, exactly as
 * `4 To pickup`'s does. Recorded as Q-141, which asks for a route-level distance in GEO-04 or in
 * EXP-003 rather than one leg per request from a handset.
 *
 * ## The map is not drawn, because the design does not draw one
 *
 * The design marks this screen `map:1` and builds five absolutely positioned layers for it, none of
 * which reaches the page: the file's own `phones` object sets `layers` twice and the bare shorthand
 * wins, so every `ly.s` the template asks for is `undefined`. The approved reference screenshots
 * agree. `ui/PhoneShell.tsx` carries the full note; Q-68 is the question.
 */

export interface MultiStopJobProps {
  readonly api: DriverApi;
  readonly tenantId: string;
  readonly driverId: string | undefined;
  /**
   * One plan named in the address, when a courier was sent to a particular one.
   *
   * Optional, unlike every other identifier this application takes from the query: ADR 0040's
   * listing means this screen can find the plan by itself. What the address is for is a notification
   * that sends a courier to a *specific* plan when they are carrying more than one — which is the
   * case Q-141 says nothing published can decide for them.
   */
  readonly planId: string | undefined;
  /** This device's position, GEO-04's first origin. Injected so it can be driven. */
  readonly position: () => Promise<GeolocationPosition>;
  readonly now: () => number;
}

interface Loaded {
  readonly plan: StopPlanRecord;
  readonly addresses: ReadonlyMap<string, AddressRecord>;
  /** The remaining route in metres, or undefined when any leg of it could not be measured. */
  readonly distanceMetresLeft: number | undefined;
  /** True when the page held plans and none of them has work left. */
  readonly carryingMoreThanOne: boolean;
}

export function MultiStopJob(props: MultiStopJobProps): ReactNode {
  const { api, tenantId, driverId, planId } = props;
  const [loaded, setLoaded] = useState<Loaded | undefined>(undefined);
  const [empty, setEmpty] = useState(false);
  const [conflict, setConflict] = useState(false);
  const [sending, setSending] = useState(false);
  const [state, setState] = useState<ViewState>({ kind: "loading" });

  const load = useCallback(() => {
    if (driverId === undefined) return;
    setState({ kind: "loading" });
    setEmpty(false);
    setConflict(false);
    api
      .stopPlansForDriver({ tenantId, driverId })
      .then(async (page) => {
        const chosen =
          planId === undefined
            ? planInProgress(page.plans)
            : page.plans.find((plan) => plan.planId === planId);
        if (chosen === undefined) {
          setLoaded(undefined);
          setEmpty(true);
          setState({ kind: "ready" });
          return;
        }
        const addresses = await resolveAddresses(api, chosen);
        const distanceMetresLeft = await remainingRoute({
          api,
          plan: chosen,
          addresses,
          position: props.position,
        });
        setLoaded({
          plan: chosen,
          addresses,
          distanceMetresLeft,
          carryingMoreThanOne: page.plans.length > 1,
        });
        setState({ kind: "ready" });
      })
      .catch((error: unknown) => {
        setState(stateForError(error, [STOP_PLAN_READ_SCOPE, ADDRESS_READ_SCOPE, DISTANCE_SCOPE]));
      });
  }, [api, tenantId, driverId, planId, props.position]);

  useEffect(load, [load]);

  /**
   * `Arrived at stop 2` — TRP-04's `arrive`, on the stop in hand.
   *
   * `expectedVersion` is the version the screen is drawing, which is what makes the command a
   * statement about the plan the courier can see rather than about whatever it has become. A 409
   * means it became something else: the plan is **re-read** and the courier is shown what it is now,
   * which is `CLAUDE.md` section 2's rule that a check-status action is a read and never a retry of
   * the command.
   *
   * The key is minted per press rather than per mount, and that is the opposite of `5 Scan`'s
   * arrangement for a reason: a scan is a sequence this device owns, where an arrival is a
   * transition TRP-04 fences with `expectedVersion`. A key held across presses would make a second,
   * genuinely different arrival replay the first.
   */
  const arrive = useCallback(() => {
    if (loaded === undefined || sending) return;
    const progress = stopProgress(loaded.plan);
    const stop = progress.current;
    if (stop === undefined) return;
    setSending(true);
    api
      .transitionStop({
        tenantId,
        planId: loaded.plan.planId,
        stopId: stop.stopId,
        action: ARRIVE_AT_STOP,
        expectedVersion: loaded.plan.version,
        occurredAt: new Date(props.now()).toISOString(),
        /*
         * `null`, because TRP-04 requires the field and requires a reason only for a skip.
         *
         * Published, required and nullable — so sending `null` is answering the contract rather
         * than omitting a field, and inventing a sentence for an arrival nobody asked a reason for
         * would be putting this build's words into an audit record.
         */
        reason: null,
        idempotencyKey: globalThis.crypto.randomUUID(),
      })
      .then((plan) => {
        setLoaded((held) => (held === undefined ? held : { ...held, plan }));
        setSending(false);
      })
      .catch((error: unknown) => {
        const next = stateForError(error, [STOP_TRANSITION_SCOPE]);
        if (next.kind === "error" && next.code === "plan_version_conflict") {
          api
            .stopPlan({ tenantId, planId: loaded.plan.planId })
            .then((plan) => {
              setLoaded((held) => (held === undefined ? held : { ...held, plan }));
              setConflict(true);
              setSending(false);
            })
            .catch(() => {
              setConflict(true);
              setSending(false);
            });
          return;
        }
        setSending(false);
        setState(next);
      });
  }, [api, tenantId, loaded, sending, props.now]);

  if (driverId === undefined) {
    /*
     * No courier in the token, which is R-10's refusal to guess one.
     *
     * The listing is addressed by `driverId` and bounded by the token subject, so sending the
     * subject in place of the identifier would be answered a 404 whose cause reads as "you have no
     * plan". This screen says which fact it is instead.
     */
    return (
      <StateScreen
        screen="multi-stop-job"
        chip={{ class: "unknown", label: "no courier named" }}
        title={NO_COURIER_TITLE}
        sub={NO_COURIER_SUB}
      />
    );
  }

  if (state.kind !== "ready") {
    return (
      <StateScreen
        screen="multi-stop-job"
        state={state}
        onRetry={load}
        purpose="Looking up the multi-stop job you're carrying."
      />
    );
  }

  if (empty || loaded === undefined) {
    return (
      <StateScreen
        screen="multi-stop-job"
        chip={{ class: "waiting", label: "no job" }}
        title={NO_PLAN_TITLE}
        sub={NO_PLAN_SUB}
      />
    );
  }

  const progress = stopProgress(loaded.plan);
  const title = planTitle({ progress, addresses: loaded.addresses });
  const sub = planSubLine({ progress, distanceMetresLeft: loaded.distanceMetresLeft });
  const unavailable = arriveUnavailable(progress);
  const designed = title.designed && sub.designed && planChipIsDesigned(progress) && !conflict;

  return (
    <PhoneShell
      screen="multi-stop-job"
      /* `map:1` — the flat `C.map` hero at its own height, and nothing in it. Q-68. */
      map
      /* `on:1`, the design's own flag: a courier mid-route is on shift. */
      online
      earningsLabel={null}
      chip={conflict ? { class: "caution", label: "plan changed" } : planChip(progress)}
      title={conflict ? ARRIVAL_CONFLICT_TITLE : title.text}
      sub={conflict ? ARRIVAL_CONFLICT_SUB : sub.text}
      rows={planRows({ progress, addresses: loaded.addresses })}
      actions={[
        {
          label: arriveLabel(progress),
          glyph: "where_to_vote",
          primary: true,
          /* `slide`, the design's fourth element: _"slide-to-confirm for accept, arrive, start"_. */
          slide: true,
          ...(unavailable === undefined ? { onPress: arrive } : { unavailable }),
        },
      ]}
      {...(designed
        ? loaded.carryingMoreThanOne && planId === undefined
          ? { flag: "you are carrying more than one job" }
          : {}
        : { flag: "wording not in the design" })}
    />
  );
}

/**
 * Every distinct address the plan's stops name, resolved once each.
 *
 * Distinct rather than per stop, and settled rather than awaited together: one refused address must
 * cost one row, not the screen. `Promise.allSettled` over a set of at most twenty is the bound —
 * a plan cannot carry more stops than that, which TRP-04's domain enforces.
 */
async function resolveAddresses(
  api: DriverApi,
  plan: StopPlanRecord,
): Promise<ReadonlyMap<string, AddressRecord>> {
  const wanted = [...new Set(plan.stops.map((stop) => stop.addressId))];
  const answered = await Promise.allSettled(wanted.map((addressId) => api.address(addressId)));
  const found = new Map<string, AddressRecord>();
  answered.forEach((answer, at) => {
    if (answer.status === "fulfilled") found.set(wanted[at]!, answer.value);
  });
  return found;
}

/**
 * `6.2 km left` — the remaining route, in metres, or nothing at all.
 *
 * The sum of the legs still to travel: this device to the stop in hand, then each remaining stop to
 * the next. GEO-04 answers one pair, so a route of n remaining stops is n requests — sequential
 * rather than concurrent, because a burst of nineteen from a handset to a provider-backed service
 * for one screen is the N+1 EXP-003's README is about, and a courier carrying twenty stops is the
 * case that would produce it.
 *
 * **Any missing piece drops the whole figure.** A device with no position, an address that did not
 * resolve, a refused GEO-04 — each means the total is not a total, and a partial sum drawn as
 * `6.2 km left` is a number a courier plans their shift around. Q-141 asks for a route-level
 * distance so this is one request rather than n.
 */
async function remainingRoute(input: {
  readonly api: DriverApi;
  readonly plan: StopPlanRecord;
  readonly addresses: ReadonlyMap<string, AddressRecord>;
  readonly position: () => Promise<GeolocationPosition>;
}): Promise<number | undefined> {
  const progress = stopProgress(input.plan);
  if (progress.current === undefined) return undefined;

  let here: { latitude: number; longitude: number };
  try {
    const where = await input.position();
    here = { latitude: where.coords.latitude, longitude: where.coords.longitude };
  } catch {
    return undefined;
  }

  const ahead = [progress.current, ...progress.remaining];
  const places = ahead.map((stop) => input.addresses.get(stop.addressId));
  if (places.some((place) => place === undefined)) return undefined;

  let total = 0;
  let from = here;
  for (const place of places as readonly AddressRecord[]) {
    const to = { latitude: place.latitude, longitude: place.longitude };
    try {
      const leg = await input.api.distanceEta({
        origin: from,
        destination: to,
        mode: PICKUP_TRAVEL_MODE,
      });
      total += leg.distanceMetres;
    } catch {
      return undefined;
    }
    from = to;
  }
  return total;
}
